// pages/Notifications.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiBell, FiCheck, FiX, FiUsers, FiCalendar, 
  FiCheckCircle, FiXCircle, FiClock
} from 'react-icons/fi';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import DashboardLayout from '../components/DashboardLayout';
import '../styles/Notifications.scss';

// Map notification type → icon + colour
const TYPE_META = {
  cohost_invitation: { icon: FiUsers,       color: '#7C4DFF', label: 'Co-host Invitation' },
  cohost_accepted:   { icon: FiCheckCircle, color: '#4CD964', label: 'Co-host Accepted'   },
  cohost_declined:   { icon: FiXCircle,     color: '#FF3B30', label: 'Co-host Declined'   },
  rsvp:              { icon: FiCalendar,    color: '#007AFF', label: 'RSVP'               },
  event_update:      { icon: FiCalendar,    color: '#FF9F0A', label: 'Event Update'       },
  default:           { icon: FiBell,        color: '#8E8E93', label: 'Notification'       },
};

const getMeta = (type) => TYPE_META[type] || TYPE_META.default;

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  <  1) return 'just now';
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days  <  7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

export default function Notifications() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [actionLoading, setActionLoading] = useState({}); // notif.id → true

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(60);

    if (!error && data) setNotifications(data);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // ── real-time ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('notifications-page')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        setNotifications(prev => [payload.new, ...prev]);
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        setNotifications(prev =>
          prev.map(n => n.id === payload.new.id ? payload.new : n)
        );
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [user]);

  // ── mark as read ───────────────────────────────────────────────────────────
  const markRead = async (id) => {
    await supabase.from('notifications').update({ read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllRead = async () => {
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  // ── cohost accept / decline ────────────────────────────────────────────────
  const handleCohostResponse = async (notification, accept) => {
    const data       = notification.data || {};
    const eventId    = data.eventId    || notification.event_id;
    const financialYear = data.financialYear;
    const invitedBy  = data.invitedBy;
    const eventTitle = data.eventTitle || 'the event';
    const coHostName = `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() || 'Co-host';

    setActionLoading(prev => ({ ...prev, [notification.id]: true }));

    try {
      // 1. Update event_cohosts status
      const updateData = {
        status: accept ? 'accepted' : 'rejected',
        responded_at: new Date().toISOString(),
        ...(accept && { accepted_at: new Date().toISOString() }),
      };

      const query = supabase
        .from('event_cohosts')
        .update(updateData)
        .eq('event_id', eventId)
        .eq('user_id', user.id);

      if (financialYear) query.eq('financial_year', financialYear);

      const { error: updateError } = await query;
      if (updateError) throw updateError;

      // 2. Notify the host of the response
      if (invitedBy) {
        // Through the function: `notifications` takes no client inserts any
        // more, because a notification written by anyone to anyone is a
        // phishing surface. Same call the app makes.
        await supabase.rpc('create_notification_secure', {
          p_user_id: invitedBy,
          p_type: accept ? 'cohost_accepted' : 'cohost_declined',
          p_title: accept ? 'Co-host Invitation Accepted' : 'Co-host Invitation Declined',
          p_message: `${coHostName} ${accept ? 'accepted' : 'declined'} your co-host invitation for "${eventTitle}"`,
          p_event_id: eventId,
          p_data: { eventId, financialYear, eventTitle },
        });
      }

      // 3. Mark this notification as read + update local type so buttons disappear
      await supabase.from('notifications').update({
        read: true,
        type: accept ? 'cohost_accepted' : 'cohost_declined',
      }).eq('id', notification.id);

      setNotifications(prev => prev.map(n =>
        n.id === notification.id
          ? { ...n, read: true, type: accept ? 'cohost_accepted' : 'cohost_declined' }
          : n
      ));
    } catch (err) {
      console.error('Error responding to cohost invite:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [notification.id]: false }));
    }
  };

  // ── navigate to event ──────────────────────────────────────────────────────
  const handleNotifClick = (notif) => {
    if (!notif.read) markRead(notif.id);
    const eventId = notif.data?.eventId || notif.event_id;
    if (eventId && notif.data?.slug) navigate(`/i/${notif.data.slug}`);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="notifications-page">
        <div className="notifications-header">
          <div className="notifications-title">
            <FiBell size={22} />
            <h1>Notifications</h1>
            {unreadCount > 0 && (
              <span className="unread-badge">{unreadCount}</span>
            )}
          </div>
          {unreadCount > 0 && (
            <button className="mark-all-read-btn" onClick={markAllRead}>
              <FiCheck size={14} />
              Mark all read
            </button>
          )}
        </div>

        {loading ? (
          <div className="notifications-loading">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="notif-skeleton" />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">
            <FiBell size={48} />
            <p>No notifications yet</p>
            <span>You'll see invitations, event updates, and more here.</span>
          </div>
        ) : (
          <div className="notifications-list">
            <AnimatePresence>
              {notifications.map((notif) => {
                const meta    = getMeta(notif.type);
                const Icon    = meta.icon;
                const isPending = notif.type === 'cohost_invitation' && !notif.read;
                const isActioned = notif.type === 'cohost_accepted' || notif.type === 'cohost_declined';
                const busy   = actionLoading[notif.id];

                return (
                  <motion.div
                    key={notif.id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    className={`notif-item ${!notif.read ? 'unread' : ''} ${isPending ? 'pending-action' : ''}`}
                    onClick={() => !isPending && handleNotifClick(notif)}
                  >
                    {/* Unread dot */}
                    {!notif.read && <span className="unread-dot" />}

                    {/* Icon */}
                    <div className="notif-icon" style={{ background: `${meta.color}22`, color: meta.color }}>
                      <Icon size={20} />
                    </div>

                    {/* Content */}
                    <div className="notif-content">
                      <div className="notif-top">
                        <span className="notif-title">{notif.title || meta.label}</span>
                        <span className="notif-time">
                          <FiClock size={11} />
                          {timeAgo(notif.created_at)}
                        </span>
                      </div>
                      <p className="notif-message">{notif.message}</p>

                      {/* Cohost invite action buttons */}
                      {isPending && (
                        <div className="notif-actions" onClick={e => e.stopPropagation()}>
                          <button
                            className="notif-btn accept"
                            disabled={busy}
                            onClick={() => handleCohostResponse(notif, true)}
                          >
                            {busy ? <span className="btn-spinner" /> : <FiCheck size={14} />}
                            Accept
                          </button>
                          <button
                            className="notif-btn decline"
                            disabled={busy}
                            onClick={() => handleCohostResponse(notif, false)}
                          >
                            <FiX size={14} />
                            Decline
                          </button>
                        </div>
                      )}

                      {/* Already actioned label */}
                      {isActioned && (
                        <span className={`notif-actioned-label ${notif.type}`}>
                          {notif.type === 'cohost_accepted' ? '✓ You accepted' : '✗ You declined'}
                        </span>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
